/**
 * ServiceStore — the Service Directory feature's data-access wrapper.
 *
 * Strict layering: this is the ONLY services module that talks to the API, and
 * it does so exclusively through the {@link EngineeringVectorApi} facade
 * (`api.documents<Service>('services')` + `api.identityProvider`). It never
 * imports ApiClient or calls fetch. React hooks call this store; pages call the
 * hook.
 *
 * Persistence model: each service is one document with id `svc_<uuid>` in the
 * real 'services' generic-store collection (DDB pointer row + S3 body). Every
 * field is short, so every field is mirrored onto the DDB pointer row's index
 * attributes and {@link listSummaries} reconstructs full service summaries
 * directly from the pointer rows WITHOUT hydrating S3 bodies.
 *
 * Deletion: the API layer exposes no delete op, so {@link softDelete} sets a
 * `deleted` flag on the document and its pointer row; queries filter it out.
 *
 * NO backend changes are required: this reuses the generic document store.
 */

import type { EngineeringVectorApi } from '../../api';
import type { DocumentStore } from '../../api';
import type { StringMap } from '../../api';
import type { Service, ServiceDraft, ServiceSummary } from './types';
import { serviceDocId } from './serviceModel';

export const SERVICES_COLLECTION = 'services';

/** Delimiter used to mirror string[] fields onto a single pointer-row attribute. */
const LIST_SEP = '\n';

const joinList = (list?: string[]): string => (list ?? []).join(LIST_SEP);
const splitList = (raw?: string): string[] =>
  (raw ?? '')
    .split(LIST_SEP)
    .map((s) => s.trim())
    .filter(Boolean);

/** Pointer-row index attribute keys. */
const ROW = {
  primaryIdentifier: 'primaryIdentifier',
  collection: 'collection',
  name: 'name',
  purpose: 'purpose',
  owningTeam: 'owningTeam',
  primaryPoc: 'primaryPoc',
  secondaryPoc: 'secondaryPoc',
  repositories: 'repositories',
  currentGoalIds: 'currentGoalIds',
  activeWorkstreamIds: 'activeWorkstreamIds',
  documentIds: 'documentIds',
  recentDecisionIds: 'recentDecisionIds',
  knownDependencies: 'knownDependencies',
  createdBy: 'createdBy',
  createdAt: 'createdAt',
  lastUpdated: 'lastUpdated',
  deleted: 'deleted',
} as const;

export class ServiceStore {
  private readonly docs: DocumentStore<Service>;

  constructor(private readonly api: EngineeringVectorApi) {
    this.docs = api.documents<Service>(SERVICES_COLLECTION);
  }

  /** Builds the queryable index attributes mirrored onto the pointer row. */
  private indexAttributes(service: Service): StringMap {
    return {
      [ROW.name]: service.name ?? '',
      [ROW.purpose]: service.purpose ?? '',
      [ROW.owningTeam]: service.owningTeam ?? '',
      [ROW.primaryPoc]: service.primaryPoc ?? '',
      [ROW.secondaryPoc]: service.secondaryPoc ?? '',
      [ROW.repositories]: joinList(service.repositories),
      [ROW.currentGoalIds]: joinList(service.currentGoalIds),
      [ROW.activeWorkstreamIds]: joinList(service.activeWorkstreamIds),
      [ROW.documentIds]: joinList(service.documentIds),
      [ROW.recentDecisionIds]: joinList(service.recentDecisionIds),
      [ROW.knownDependencies]: joinList(service.knownDependencies),
      [ROW.createdBy]: service.createdBy ?? '',
      [ROW.createdAt]: service.createdAt ?? '',
      [ROW.lastUpdated]: service.lastUpdated ?? '',
      [ROW.deleted]: service.deleted ? 'true' : 'false',
    };
  }

  /** Reconstructs a full service summary from a pointer row. */
  private toSummary(row: StringMap): ServiceSummary {
    return {
      id: row[ROW.primaryIdentifier] ?? '',
      name: row[ROW.name] ?? '',
      purpose: row[ROW.purpose] ?? '',
      owningTeam: row[ROW.owningTeam] ?? '',
      primaryPoc: row[ROW.primaryPoc] ?? '',
      secondaryPoc: row[ROW.secondaryPoc] || undefined,
      repositories: splitList(row[ROW.repositories]),
      currentGoalIds: splitList(row[ROW.currentGoalIds]),
      activeWorkstreamIds: splitList(row[ROW.activeWorkstreamIds]),
      documentIds: splitList(row[ROW.documentIds]),
      recentDecisionIds: splitList(row[ROW.recentDecisionIds]),
      knownDependencies: splitList(row[ROW.knownDependencies]),
      createdBy: row[ROW.createdBy] ?? '',
      createdAt: row[ROW.createdAt] ?? '',
      lastUpdated: row[ROW.lastUpdated] ?? '',
    };
  }

  /** Lists all non-deleted service summaries (defensively scoped to this collection). */
  async listSummaries(): Promise<ServiceSummary[]> {
    const rows = await this.docs.list();
    return rows
      .filter(
        (r) =>
          r[ROW.collection] === SERVICES_COLLECTION &&
          r[ROW.deleted] !== 'true' &&
          !!r[ROW.primaryIdentifier],
      )
      .map((r) => this.toSummary(r));
  }

  /** Loads a single service document by id (hydrates the S3 body). */
  async get(id: string): Promise<Service | undefined> {
    return this.docs.get(id);
  }

  /**
   * Registers a new service. Seeds id, createdBy (from identity), createdAt and
   * lastUpdated, and returns the saved document.
   */
  async create(draft: ServiceDraft): Promise<Service> {
    const identity = await this.api.identityProvider.getIdentity();
    const service = this.fromDraft(serviceDocId(), draft, identity.alias, undefined);
    await this.docs.save(service.id, service, this.indexAttributes(service));
    return service;
  }

  /**
   * Applies a draft's fields to an existing service in place (same id/createdBy/
   * createdAt), refreshes lastUpdated, and re-mirrors index attributes.
   */
  async applyDraft(service: Service, draft: ServiceDraft): Promise<Service> {
    const updated = this.fromDraft(
      service.id,
      draft,
      service.createdBy,
      service.createdAt,
    );
    return this.update(updated);
  }

  /** Persists a full service document in place (refreshes lastUpdated). */
  async update(service: Service): Promise<Service> {
    const updated: Service = {
      ...service,
      lastUpdated: new Date().toISOString(),
      deleted: false,
    };
    await this.docs.update(updated.id, updated, this.indexAttributes(updated));
    return updated;
  }

  /**
   * Soft-deletes a service: fetches it, flags it deleted, and updates both the
   * S3 body and the pointer row so queries filter it out.
   */
  async softDelete(id: string): Promise<void> {
    const existing = await this.docs.get(id);
    if (!existing) return;
    const deleted: Service = { ...existing, deleted: true };
    await this.docs.update(id, deleted, this.indexAttributes(deleted));
  }

  /**
   * Builds a full Service from a draft, seeding id/createdBy and preserving
   * createdAt on edit (new entries stamp "now"). Empty optional text fields
   * collapse to undefined.
   */
  private fromDraft(
    id: string,
    draft: ServiceDraft,
    createdBy: string,
    existingCreatedAt: string | undefined,
  ): Service {
    const now = new Date().toISOString();
    const clean = (s?: string) => {
      const t = (s ?? '').trim();
      return t ? t : undefined;
    };
    return {
      id,
      name: draft.name.trim(),
      purpose: draft.purpose.trim(),
      owningTeam: draft.owningTeam.trim(),
      primaryPoc: draft.primaryPoc.trim(),
      secondaryPoc: clean(draft.secondaryPoc),
      repositories: draft.repositories ?? [],
      currentGoalIds: draft.currentGoalIds ?? [],
      activeWorkstreamIds: draft.activeWorkstreamIds ?? [],
      documentIds: draft.documentIds ?? [],
      recentDecisionIds: draft.recentDecisionIds ?? [],
      knownDependencies: draft.knownDependencies ?? [],
      createdBy,
      createdAt: existingCreatedAt ?? now,
      lastUpdated: now,
      deleted: false,
    };
  }
}
